import React from 'react';
import Icon from 'components/AppIcon';
import WaveBackground from '../login-screen/components/WaveBackground';

// Landing for the access links that provision-client.ts (easytek-ai) hands out: a new client's
// invite, or a recovery link for an account that already exists.
//
// It shows WHOSE account the link opens (as Supabase reports it, never as the token claims) and
// waits for a click. Nothing is signed in before that click: the session in a link is only
// adopted by AccessLinkGate when the person presses Continue. While Supabase has not confirmed
// the account (`verificando`), there is no Continue button at all. That click is the defense against a crafted link carrying someone else's session
// (it would otherwise land in the cookie shared by every .easytek.cr tool). After Continue,
// ProtectedRoute takes over and shows "Set your password", because provisioning flags the
// account with must_change_password.
//
// tipo: 'invite' | 'recovery' | 'invalido'
const TEXTOS = {
  invite: { icon: 'UserCheck', title: 'Set up your ITSM access' },
  recovery: { icon: 'KeyRound', title: 'Reset your ITSM password' },
  invalido: { icon: 'AlertCircle', title: 'This link is no longer valid' },
};

const AccessLinkScreen = ({
  tipo,
  email,
  verificando = false,
  loading = false,
  error = '',
  onContinue,
  onRetry,
  onCancel,
}) => {
  const textos = TEXTOS[tipo] || TEXTOS.invalido;
  const invalido = !TEXTOS[tipo] || tipo === 'invalido';

  const boton = (label, onClick, primario) => (
    <button
      type="button"
      onClick={onClick}
      disabled={loading}
      className="w-full h-11 rounded-lg font-medium text-sm flex items-center justify-center gap-2 transition-all duration-200"
      style={
        primario
          ? {
              backgroundColor: loading ? 'var(--color-muted)' : 'var(--color-primary)',
              color: loading ? 'var(--color-muted-foreground)' : '#FFFFFF',
              cursor: loading ? 'not-allowed' : 'pointer',
              boxShadow: loading ? 'none' : 'var(--shadow-md)',
            }
          : {
              backgroundColor: 'transparent',
              border: '1px solid var(--color-border)',
              color: 'var(--color-foreground)',
              cursor: loading ? 'not-allowed' : 'pointer',
            }
      }
    >
      {label}
    </button>
  );

  return (
    <div
      className="dark min-h-screen flex flex-col items-center justify-center px-4"
      style={{ backgroundColor: '#080d1e', position: 'relative' }}
    >
      <WaveBackground />
      <div
        className="w-full max-w-[420px] rounded-2xl p-8"
        style={{
          backgroundColor: 'rgba(15, 23, 42, 0.82)',
          border: '1px solid rgba(34, 211, 238, 0.22)',
          boxShadow: '0 25px 60px rgba(0, 0, 0, 0.55), 0 0 0 1px rgba(52, 102, 244, 0.10)',
          backdropFilter: 'blur(18px)',
          WebkitBackdropFilter: 'blur(18px)',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <div className="flex flex-col items-center gap-4 mb-7">
          <div
            className="w-16 h-16 rounded-2xl flex items-center justify-center"
            style={{ backgroundColor: invalido ? 'rgba(220, 38, 38, 0.18)' : 'var(--color-primary)' }}
          >
            <Icon name={textos.icon} size={30} color={invalido ? 'var(--color-error)' : '#FFFFFF'} strokeWidth={2} />
          </div>
          <div className="text-center">
            <h1
              className="text-xl font-bold tracking-tight"
              style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}
            >
              {textos.title}
            </h1>
            {invalido ? (
              <p className="text-sm mt-1" style={{ color: 'var(--color-muted-foreground)' }}>
                Access links work only once and expire. Ask easyTEK for a new one.
              </p>
            ) : verificando ? (
              <p className="text-sm mt-1" style={{ color: 'var(--color-muted-foreground)' }}>
                Checking the link…
              </p>
            ) : (
              <>
                <p className="text-sm mt-1" style={{ color: 'var(--color-muted-foreground)' }}>
                  This link opens the account:
                </p>
                <p className="text-base font-medium mt-2 break-all" style={{ color: 'var(--color-foreground)' }}>
                  {email}
                </p>
              </>
            )}
          </div>
        </div>

        {invalido ? (
          boton('Go to sign in', onCancel, true)
        ) : verificando ? (
          <div className="flex flex-col gap-4">
            {error && (
              <div
                className="flex items-start gap-3 px-4 py-3 rounded-lg"
                style={{
                  backgroundColor: 'rgba(220, 38, 38, 0.08)',
                  border: '1px solid rgba(220, 38, 38, 0.25)',
                }}
              >
                <Icon name="AlertCircle" size={16} color="var(--color-error)" strokeWidth={2} className="mt-0.5 flex-shrink-0" />
                <span className="text-sm" style={{ color: 'var(--color-error)' }}>{error}</span>
              </div>
            )}
            {error && boton('Try again', onRetry, true)}
            {boton('Cancel', onCancel, false)}
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <div
              className="flex items-start gap-3 px-4 py-3 rounded-lg"
              style={{
                backgroundColor: 'rgba(217, 119, 6, 0.12)',
                border: '1px solid rgba(217, 119, 6, 0.40)',
              }}
            >
              <Icon name="ShieldAlert" size={16} color="var(--color-warning)" strokeWidth={2} className="mt-0.5 flex-shrink-0" />
              <span className="text-sm" style={{ color: 'var(--color-foreground)' }}>
                Only continue if you asked easyTEK for this link and this is your email. If it is not,
                cancel and close this page.
              </span>
            </div>
            {error && (
              <div
                className="flex items-start gap-3 px-4 py-3 rounded-lg"
                style={{
                  backgroundColor: 'rgba(220, 38, 38, 0.08)',
                  border: '1px solid rgba(220, 38, 38, 0.25)',
                }}
              >
                <Icon name="AlertCircle" size={16} color="var(--color-error)" strokeWidth={2} className="mt-0.5 flex-shrink-0" />
                <span className="text-sm" style={{ color: 'var(--color-error)' }}>{error}</span>
              </div>
            )}
            {boton(loading ? 'Opening…' : 'Continue', onContinue, true)}
            {boton('Cancel', onCancel, false)}
          </div>
        )}
      </div>
    </div>
  );
};

export default AccessLinkScreen;
